import type { TaskListMode } from "../../domain/app-types.ts";
import type { Category, CollectionViewMode, Todo } from "../../domain/entities.ts";
import { normalizeBoardInactiveDays, type BoardInactiveDays } from "./board-inactivity.ts";
import type { CollapsedBoardColumns } from "./board-columns.ts";
import { initialTimelineAnchor, shiftTimelineAnchor, validTimelineDate } from "./collection-timeline.ts";
import type { WeekStart } from "./calendar-presentation.ts";
import { normalizeTablePreferences, type TablePreferences } from "./table/preferences.ts";

export interface BoardColumnRef { collectionId: string; column: string }

export interface TaskCollectionStateOptions {
  collapsedColumns: CollapsedBoardColumns;
  listMode: TaskListMode;
  boardMode: "simple" | "detailed";
  inactiveDays: number;
}

export class TaskCollectionState {
  private readonly boardVisibleCounts = new Map<string, number>();
  private readonly timelineAnchors = new Map<string, string>();
  private readonly timelineVisibleCounts = new Map<string, number>();
  timelineUnscheduledOpen: Record<string,boolean> = {};
  collapsedColumns: CollapsedBoardColumns;
  focusedBoardColumn: BoardColumnRef | null = null;
  inlineCapture: (BoardColumnRef & { epicId?: string | null }) | null = null;
  listMode: TaskListMode;
  boardMode: "simple" | "detailed";
  selectedView: CollectionViewMode | null = null;
  listLayout: "list" | "table" = "list";
  table: TablePreferences = normalizeTablePreferences(null, false);
  inactiveDays: BoardInactiveDays;
  pageSize = 0;
  page = 1;

  constructor(options: TaskCollectionStateOptions) {
    this.collapsedColumns = options.collapsedColumns;
    this.listMode = options.listMode;
    this.boardMode = options.boardMode;
    this.inactiveDays = normalizeBoardInactiveDays(options.inactiveDays);
  }

  setInactiveDays(value: unknown): BoardInactiveDays {
    this.inactiveDays = normalizeBoardInactiveDays(value);
    return this.inactiveDays;
  }

  viewFor(category: Category): CollectionViewMode {
    return this.selectedView || category.viewType || "list";
  }

  toggleInlineCapture(collectionId: string, column: string): BoardColumnRef | null {
    const same = this.inlineCapture?.collectionId === collectionId && this.inlineCapture.column === column;
    this.inlineCapture = same ? null : { collectionId, column };
    return this.inlineCapture;
  }

  closeInlineCapture(): void {
    this.inlineCapture = null;
  }

  rememberBoardColumn(collectionId: string, column: string): void {
    this.focusedBoardColumn = { collectionId, column };
  }

  boardVisibleCount(collectionId: string, column: string): number {
    return this.boardVisibleCounts.get(`${collectionId}\u0000${column}`) || 30;
  }

  showMoreBoardTasks(collectionId: string, column: string): number {
    const previous = this.boardVisibleCount(collectionId, column);
    this.boardVisibleCounts.set(`${collectionId}\u0000${column}`, previous + 30);
    return previous;
  }

  ensureBoardTaskVisible(collectionId: string, column: string, index: number): void {
    const current = this.boardVisibleCount(collectionId, column);
    if (index < current) return;
    this.boardVisibleCounts.set(`${collectionId}\u0000${column}`, Math.ceil((index + 1) / 30) * 30);
  }

  timelineAnchor(collectionId: string, today: string, tasks: Todo[]=[], weekStart: WeekStart="monday", allTasks=tasks): string {
    if(!this.timelineAnchors.has(collectionId))this.timelineAnchors.set(collectionId,initialTimelineAnchor(tasks,today,weekStart,allTasks));
    return this.timelineAnchors.get(collectionId)!;
  }

  setTimelineAnchor(collectionId: string, date: string): void {
    if (validTimelineDate(date)) this.timelineAnchors.set(collectionId, date);
  }

  shiftTimeline(collectionId: string, today: string, windows: number): void {
    this.setTimelineAnchor(collectionId, shiftTimelineAnchor(this.timelineAnchor(collectionId, today), windows));
  }

  resetTimeline(collectionId: string): void {
    this.timelineAnchors.delete(collectionId);
  }

  timelineVisibleCount(collectionId: string): number {
    return this.timelineVisibleCounts.get(collectionId) || 100;
  }

  showMoreTimeline(collectionId: string): void {
    this.timelineVisibleCounts.set(collectionId, this.timelineVisibleCount(collectionId) + 100);
  }

  resetPage(): void {
    this.page = 1;
  }
}
